import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  CircularProgress,
  Alert,
  Stack,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import api from '../../utils/api';

const CONTACT_TYPE_LABELS = {
  candidate: 'Candidate',
  employee: 'Employee',
  contractor: 'Contractor',
};

const B1_CONTACT_TYPES = ['candidate', 'employee', 'contractor'];

function contactLabel(c) {
  if (!c) return '';
  const name = `${c.first_name || ''} ${c.last_name || ''}`.trim();
  const t = CONTACT_TYPE_LABELS[c.contact_type] || c.contact_type;
  return `${name} — ${c.email || ''} (${t})`;
}

function phoneDisplay(c) {
  if (!c) return '';
  return c.phone || c.mobile || '';
}

export default function ProcessB1List() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [page, setPage] = useState(1);

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [contactOptions, setContactOptions] = useState([]);
  const [contactLoading, setContactLoading] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [notes, setNotes] = useState('');

  const loadCases = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/immigration/b1-cases', { params: { page, limit: 25 } });
      setRows(res.data?.data || []);
      const p = res.data?.pagination;
      if (p) {
        setPagination({ page: p.page, totalPages: p.totalPages, total: p.total });
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load B1 cases.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadCases();
  }, [loadCases]);

  const loadEligibleContacts = useCallback(async () => {
    setContactLoading(true);
    try {
      const results = await Promise.all(
        B1_CONTACT_TYPES.map((contact_type) =>
          api.get('/contacts', {
            params: { contact_type, status: 'active', limit: 500, page: 1 },
          })
        )
      );
      const byId = new Map();
      results.forEach((r) => {
        (r.data?.data || []).forEach((c) => {
          if (!byId.has(c.id)) byId.set(c.id, c);
        });
      });
      const merged = Array.from(byId.values()).sort((a, b) => {
        const an = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
        const bn = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase();
        return an.localeCompare(bn);
      });
      setContactOptions(merged);
    } catch {
      setContactOptions([]);
    } finally {
      setContactLoading(false);
    }
  }, []);

  const openDialog = () => {
    setFormError('');
    setSelectedContact(null);
    setNotes('');
    setOpen(true);
    loadEligibleContacts();
  };

  const submit = async () => {
    setFormError('');
    if (!selectedContact) {
      setFormError('Select a contact (Employee, Contractor, or Candidate).');
      return;
    }
    setSaving(true);
    try {
      await api.post('/immigration/b1-cases', {
        contact_id: selectedContact.id,
        notes: notes.trim() || undefined,
      });
      setOpen(false);
      loadCases();
    } catch (e) {
      setFormError(e.response?.data?.message || 'Failed to create B1 case.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        Process B1
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Record B1 (business visitor) processing. Contacts must be type Candidate, Employee, or Contractor.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openDialog}>
              Add
            </Button>
          </Stack>

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Contact type</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No B1 cases yet. Click Add to create one.
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => {
                      const c = row.contact;
                      const name = c ? `${c.first_name || ''} ${c.last_name || ''}`.trim() : '—';
                      const ct = c?.contact_type
                        ? CONTACT_TYPE_LABELS[c.contact_type] || c.contact_type
                        : '—';
                      return (
                        <TableRow key={row.id}>
                          <TableCell>{name}</TableCell>
                          <TableCell>
                            {c?.contact_type ? (
                              <Chip size="small" label={ct} variant="outlined" />
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell>{c?.email || '—'}</TableCell>
                          <TableCell>{c ? phoneDisplay(c) : '—'}</TableCell>
                          <TableCell>
                            {row.createdAt || row.created_at
                              ? new Date(row.createdAt || row.created_at).toLocaleString()
                              : '—'}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {pagination.totalPages > 1 && (
            <Stack direction="row" spacing={2} sx={{ mt: 2 }} alignItems="center">
              <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                Previous
              </Button>
              <Typography variant="body2">
                Page {page} of {pagination.totalPages} ({pagination.total} total)
              </Typography>
              <Button
                size="small"
                disabled={page >= pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New B1 case</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              options={contactOptions}
              loading={contactLoading}
              getOptionLabel={(o) => contactLabel(o)}
              value={selectedContact}
              onChange={(_, v) => setSelectedContact(v)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Contact (Candidate, Employee, or Contractor)"
                  size="small"
                  helperText="Only active contacts with these types are listed."
                />
              )}
            />

            {selectedContact && (
              <>
                <TextField label="First name" size="small" value={selectedContact.first_name || ''} disabled fullWidth />
                <TextField label="Last name" size="small" value={selectedContact.last_name || ''} disabled fullWidth />
                <TextField label="Email" size="small" value={selectedContact.email || ''} disabled fullWidth />
                <TextField label="Phone" size="small" value={phoneDisplay(selectedContact)} disabled fullWidth />
                <TextField
                  label="Contact type"
                  size="small"
                  value={CONTACT_TYPE_LABELS[selectedContact.contact_type] || selectedContact.contact_type || ''}
                  disabled
                  fullWidth
                />
              </>
            )}

            <TextField
              label="Notes (optional)"
              size="small"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submit} disabled={saving}>
            {saving ? 'Saving…' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
